import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from '@sales-center/components'
import { produce } from 'immer'

import {
  selectOpportunityById,
  selectOpportunityDetail,
  SoldServiceAppDataModel,
} from '@user/store/opportunity'
import { useAppSelector } from '@user/store/hooks'

import {
  getSavedAppData,
  getCustomFieldAppData,
  mergeCustomFieldValues,
} from '../functions'
import { SoldServicesFormModel } from '../typings'
import { ServiceConfig } from '@user/store/services/config'
import { get } from '@user/store/services/crud'

type State = {
  status: 'pending' | 'success' | 'fail'
  appData: SoldServiceAppDataModel[]
  initialValues: SoldServicesFormModel
}

type Params = {
  readonly: boolean
  entityId: number
  branchId?: string
}

export function useGetAppDataOnMount(params: Params) {
  const { entityId, readonly, branchId } = params

  const [state, setState] = useState<State>(() => ({
    appData: [],
    status: 'pending',
    initialValues: {
      salesNote: '',
      customFields: {},
    },
  }))

  const opportunity = useAppSelector((state) =>
    selectOpportunityById(state, entityId),
  )

  const opportunityDetail = useSelector(selectOpportunityDetail)

  const detailIsSameOpp = entityId === opportunityDetail?.id

  const initialSalesNote = useMemo(() => {
    let referralNote = '',
      savedSalesNote: string | null = ''

    if (!!opportunity) {
      const {
        salesNote,
        referralDiscountCode,
        referralLocationCode,
        referralValue,
      } = opportunity

      savedSalesNote = salesNote

      const referralFrom = referralLocationCode
        ? referralLocationCode
        : referralValue

      if (referralFrom && referralDiscountCode) {
        referralNote = `Referral from: ${referralFrom} - Referral code: ${referralDiscountCode}`
      }
      if (referralFrom && !referralDiscountCode) {
        referralNote = `Referral from: ${referralFrom}`
      }
    }
    if (detailIsSameOpp) {
      const {
        salesNote,
        referralSummary: {
          referralDiscountCode,
          referralLocationCode,
          referralValue,
        },
      } = opportunityDetail

      savedSalesNote = salesNote

      const referralFrom = referralLocationCode
        ? referralLocationCode
        : referralValue

      if (referralFrom && referralDiscountCode) {
        referralNote = `Referral from: ${referralFrom} - Referral code: ${referralDiscountCode}`
      }
      if (referralFrom && !referralDiscountCode) {
        referralNote = `Referral from: ${referralFrom}`
      }
    }

    return savedSalesNote !== null && savedSalesNote.trim().length > 0
      ? savedSalesNote
      : referralNote
  }, [detailIsSameOpp, opportunity, opportunityDetail])

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      try {
        const savedResult = await getSavedAppData(entityId)
        const result = await getCustomFieldAppData(entityId)

        if (!mounted) return

        const appData = readonly ? savedResult.appData : result.appData

        const { customFields: defaultValues } = result
        const { customFields: savedValues } = savedResult

        const customFields = readonly
          ? savedValues
          : mergeCustomFieldValues(defaultValues, savedValues, appData)

        setState((state) =>
          produce(state, (draft) => {
            draft.status = 'success'
            draft.appData = appData
            draft.initialValues.customFields = customFields
            draft.initialValues.salesNote = initialSalesNote
          }),
        )
      } catch (e) {
        if (mounted) {
          toast.error(e)

          setState((status) =>
            produce(status, (draft) => {
              draft.status = 'fail'
            }),
          )
        }
      }
    }
    if (branchId === undefined) execute()

    return () => {
      mounted = false
    }
  }, [entityId, readonly, initialSalesNote, branchId])

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      try {
        const response = await get(
          `${ServiceConfig.baseAPIUrl}/branch/${branchId}${ServiceConfig.branch.soldService.appData}`,
        )

        if (!mounted) return

        if (response.ok) {
          const appData = await response.json()
          setState((state) =>
            produce(state, (draft) => {
              draft.status = 'success'
              draft.appData = appData
              draft.initialValues.customFields = {}
              draft.initialValues.salesNote = initialSalesNote
            }),
          )
        }
      } catch (e) {
        if (mounted) {
          toast.error(e)

          setState((status) =>
            produce(status, (draft) => {
              draft.status = 'fail'
            }),
          )
        }
      }
    }

    if (!!branchId) execute()

    return () => {
      mounted = false
    }
  }, [branchId, initialSalesNote])

  return state
}
